import { isMackerelRegional, withMackerelScope, regionalConfidence, hasDistinctLegacyIndex } from "../../features/fishingIntelligence/mackerelCapabilities";
import React, { useEffect, useMemo, useState } from "react";
import { formatLocalDayTick, lightCycleBands, selectLocalDayHours, selectRollingHours, windowLightContext } from "../../features/fishingIntelligence/hourlyChart";
import { graphScore, isBackendPeriodPolicy, MACKEREL_RANKING_POLICY } from "../../features/fishingIntelligence/seaBassPresentation";
import {
  hasFlatDisplayedOpportunity,
} from "../../features/fishingIntelligence/multiDayForecast";

const CHART_WIDTH = 640;
const CHART_HEIGHT = 220;
const CHART_MARGIN = { top: 20, right: 42, bottom: 38, left: 64 };
const Y_TICKS = [0, 25, 50, 75, 100];
const dayX = (timestamp, axis) => CHART_MARGIN.left + (timestamp - axis.start) / (axis.end - axis.start) * (CHART_WIDTH - CHART_MARGIN.left - CHART_MARGIN.right);

const isScore = (value) =>
  Number.isFinite(value?.score) &&
  value.score >= 0 &&
  value.score <= 100 &&
  typeof value.label === "string";

const isConfidence = (value) =>
  Number.isFinite(value?.score) &&
  value.score >= 0 &&
  value.score <= 100 &&
  typeof value.level === "string";

const timestampMillis = (value) => {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value !== "string" || !value.trim()) return null;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export const isCurrentForecastHour = (timestamp, nowTimestamp = Date.now()) => {
  const pointMs = timestampMillis(timestamp);
  const nowMs = timestampMillis(nowTimestamp);
  return (
    pointMs !== null &&
    nowMs !== null &&
    nowMs >= pointMs &&
    nowMs < pointMs + 60 * 60 * 1000
  );
};

const supportsTimeZone = (timeZone) => {
  if (typeof timeZone !== "string" || !timeZone.trim()) return false;
  try {
    new Intl.DateTimeFormat("en", { timeZone }).format(0);
    return true;
  } catch (_error) {
    return false;
  }
};

const wallClockDate = (timestamp) => {
  const match = String(timestamp || "").match(/T(\d{2}):(\d{2})/);
  if (!match) return null;
  return new Date(Date.UTC(2020, 0, 1, Number(match[1]), Number(match[2])));
};

export const formatForecastHour = (
  timestamp,
  forecastTimezone,
  locale = "en",
  compact = false,
  includeTimeZoneName = false
) => {
  const parsed = timestampMillis(timestamp);
  if (parsed === null) return null;
  const useForecastZone = supportsTimeZone(forecastTimezone);
  const date = useForecastZone ? new Date(parsed) : wallClockDate(timestamp);
  if (!date) return null;
  return new Intl.DateTimeFormat(locale || "en", {
    hour: "numeric",
    ...(compact ? {} : { minute: "2-digit" }),
    ...(includeTimeZoneName ? { timeZoneName: "short" } : {}),
    timeZone: useForecastZone ? forecastTimezone : "UTC",
  }).format(date);
};

export const scoreToChartY = (score) => {
  if (!Number.isFinite(score) || score < 0 || score > 100) return null;
  const plotHeight = CHART_HEIGHT - CHART_MARGIN.top - CHART_MARGIN.bottom;
  return CHART_MARGIN.top + ((100 - score) / 100) * plotHeight;
};

export const prepareTimelinePoints = (points) => {
  const prepared = (Array.isArray(points) ? points : [])
    .map((point, sourceIndex) => ({
      ...point,
      sourceIndex,
      timestampMs: timestampMillis(point?.timestamp),
    }))
    .filter((point) => point.timestampMs !== null)
    .sort((left, right) => left.timestampMs - right.timestampMs);
  if (!prepared.length) return [];

  const firstTime = prepared[0].timestampMs;
  const lastTime = prepared[prepared.length - 1].timestampMs;
  const plotWidth = CHART_WIDTH - CHART_MARGIN.left - CHART_MARGIN.right;
  return prepared.map((point, index) => ({
    ...point,
    x:
      firstTime === lastTime
        ? CHART_MARGIN.left + plotWidth / 2
        : CHART_MARGIN.left +
          ((point.timestampMs - firstTime) / (lastTime - firstTime)) * plotWidth,
    chartIndex: index,
  }));
};

export const buildSeriesSegments = (points, field) => {
  const segments = [];
  let segment = [];
  points.forEach((point) => {
    if (point.breakBefore?.[field] && segment.length) {
      segments.push(segment);
      segment = [];
    }
    const value = point?.[field];
    if (isScore(value)) {
      segment.push({ x: point.x, y: scoreToChartY(graphScore(value)), score: graphScore(value) });
      return;
    }
    if (segment.length) segments.push(segment);
    segment = [];
  });
  if (segment.length) segments.push(segment);
  return segments;
};

export const highestOpportunityPoints = (points, limit = 3) =>
  points
    .filter((point) => isScore(point.fishingOpportunity))
    .sort((left, right) => {
      const opportunityDifference =
        right.fishingOpportunity.score - left.fishingOpportunity.score;
      if (opportunityDifference) return opportunityDifference;
      const leftConfidence = isConfidence(left.confidence) ? left.confidence.score : -1;
      const rightConfidence = isConfidence(right.confidence) ? right.confidence.score : -1;
      if (rightConfidence !== leftConfidence) return rightConfidence - leftConfidence;
      return left.timestampMs - right.timestampMs;
    })
    .slice(0, limit);

const scoreText = (value, t) => {
  if (!isScore(value)) return null;
  return `${t("fishing_today_intelligence_score_value", { score: value.score })} · ${t(
    `fishing_today_intelligence_score_${value.label}`
  )}`;
};

const confidenceText = (value, t) => {
  if (!isConfidence(value)) return null;
  return `${t("fishing_today_intelligence_score_value", { score: value.score })} · ${t(
    `fishing_today_intelligence_confidence_${value.level}`
  )}`;
};

const pointMetrics = (point, t, regional) => regional ? [
    [t("mackerel_regional_availability"), scoreText(point.availability, t)],
    ...(hasDistinctLegacyIndex(point) ? [[t("mackerel_legacy_index"), scoreText(point.fishingOpportunity, t)]] : []),
    [t("fishing_today_intelligence_activity"), t("mackerel_not_modelled")],
    [t("mackerel_regional_confidence"), confidenceText(regionalConfidence(point), t)],
  ] : [
    [t("fishing_today_intelligence_opportunity"), scoreText(point.fishingOpportunity, t)],
    [t("fishing_today_intelligence_activity"), scoreText(point.activity, t)],
    [t("fishing_today_intelligence_availability"), scoreText(point.availability, t)],
    [t("fishing_today_intelligence_confidence"), confidenceText(point.confidence, t)],
  ];

const pointAriaLabel = (point, time, t, regional) => {
  const values = pointMetrics(point, t, regional)
    .filter(([, value]) => value)
    .map(([label, value]) =>
      t("fishing_today_intelligence_accessible_metric", { label, value })
    )
    .join(". ");
  return t("fishing_today_intelligence_hour_aria", {
    time,
    values: values || t("fishing_today_intelligence_unavailable"),
  });
};

const Series = ({ field, name, points }) => {
  const segments = buildSeriesSegments(points, field);
  return (
    <g className={`rp-species-timeline__series rp-species-timeline__series--${name}`}>
      {segments.map((segment, index) =>
        segment.length > 1 ? (
          <polyline
            data-testid={`${name}-segment`}
            data-series={name}
            fill="none"
            key={`${name}-${index}`}
            points={segment.map((point) => `${point.x},${point.y}`).join(" ")}
          />
        ) : null
      )}
      {points.map((point) => {
        const value = point[field];
        return isScore(value) ? (
          <circle
            className="rp-species-timeline__marker"
            cx={point.x}
            cy={scoreToChartY(graphScore(value))}
            data-score={graphScore(value)}
            data-series={name}
            key={`${name}-${point.timestamp}`}
            r={2.5}
          />
        ) : null;
      })}
    </g>
  );
};

const SelectedDetails = ({ point, t, time, regional }) => {
  const details = pointMetrics(point, t, regional).filter(([, value]) => value);
  return (
    <div className="rp-species-timeline__selected" aria-live="polite">
      <span>{t("fishing_today_intelligence_selected_hour")}</span>
      <strong>{time}</strong>
      <dl>
        {details.map(([label, value]) => (
          <div key={label}>
            <dt>{label}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
};

export default function SpeciesIntelligenceTimeline({
  timeline,
  context,
  currentTimestamp,
  forecastTimezone,
  localDate,
  lightCycle,
  lightCycleDays,
  windowStart,
  windowEnd,
  preferredTimestamp,
  summaryPoints,
  highestHoursLabelKey = "fishing_today_intelligence_highest_opportunity_hours",
  locale = "en",
  nowTimestamp = Date.now(),
  onSelectedPointChange,
  t,
}) {
  t = withMackerelScope(t, context);
  const regional = isMackerelRegional(context);
  const day = useMemo(
    () => windowStart ? selectRollingHours(timeline?.points, forecastTimezone, windowStart, windowEnd)
      : selectLocalDayHours(timeline?.points, forecastTimezone, localDate, currentTimestamp),
    [timeline?.points, forecastTimezone, localDate, currentTimestamp, windowStart, windowEnd]
  );
  const points = useMemo(() => day.axis ? day.hours.map((point) => ({ ...point, x: dayX(point.timestampMs, day.axis) })) : [], [day]);
  const allPoints = points;
  const distinctLegacy = regional && (timeline?.rankingBasis === "availability_based_outlook" || points.some(hasDistinctLegacyIndex));
  const lightContext = useMemo(() => windowStart ? windowLightContext(day.axis, forecastTimezone, lightCycleDays)
    : { bands: lightCycleBands(lightCycle, day.axis), midnights: [], missing: false }, [windowStart, day.axis, forecastTimezone, lightCycleDays, lightCycle]);
  const { bands, midnights } = lightContext;
  const pointKey = points.map((point) => point.timestamp).join("|");
  const repeatedLocalHours = useMemo(() => {
    const counts = new Map();
    allPoints.forEach((point) => {
      const label = formatForecastHour(
        point.timestamp,
        forecastTimezone,
        locale
      );
      counts.set(label, (counts.get(label) || 0) + 1);
    });
    return new Set(
      Array.from(counts.entries())
        .filter(([, count]) => count > 1)
        .map(([label]) => label)
    );
  }, [forecastTimezone, locale, allPoints]);
  const displayHour = (point, compact = false) => {
    const regular = formatForecastHour(
      point.timestamp,
      forecastTimezone,
      locale
    );
    const time = formatForecastHour(
      point.timestamp,
      forecastTimezone,
      locale,
      compact,
      repeatedLocalHours.has(regular)
    );
    return windowStart ? `${new Intl.DateTimeFormat(locale, { timeZone: forecastTimezone, month: "short", day: "numeric" }).format(new Date(point.timestamp))}, ${time}` : time;
  };
  const currentCandidate =
    allPoints.find((point) => point.timestamp === currentTimestamp) || null;
  const currentPoint =
    currentCandidate && isCurrentForecastHour(currentCandidate.timestamp, nowTimestamp)
      ? currentCandidate
      : null;
  const initialPoint = points.find((point) => point.timestamp === currentPoint?.timestamp)
    || (currentPoint && points.find((point) => point.timestampMs >= currentPoint.timestampMs))
    || points[0] || null;
  const [selectedTimestamp, setSelectedTimestamp] = useState(preferredTimestamp || initialPoint?.timestamp || null);

  useEffect(() => {
    setSelectedTimestamp((previous) => points.some((point) => point.timestamp === previous) ? previous
      : points.some((point) => point.timestamp === preferredTimestamp) ? preferredTimestamp : initialPoint?.timestamp || null);
  }, [pointKey, preferredTimestamp, initialPoint?.timestamp, points]);

  const selectedPoint =
    allPoints.find((point) => point.timestamp === selectedTimestamp) || initialPoint;
  useEffect(() => {
    if (onSelectedPointChange) onSelectedPointChange(selectedPoint || null);
  }, [onSelectedPointChange, selectedPoint]);
  const backendRanking = isBackendPeriodPolicy(timeline?.rankingPolicy);
  const mackerelRanking = regional && timeline?.rankingPolicy === MACKEREL_RANKING_POLICY;
  const mackerelMeanLabel = distinctLegacy ? "mackerel_period_average_outlook" : "mackerel_period_average_availability";
  const rankingPoints = summaryPoints || allPoints;
  const highest = backendRanking
    ? rankingPoints.filter((point) => timeline.modeledPeriod?.pointTimestamps.includes(point.timestamp))
    : highestOpportunityPoints(prepareTimelinePoints(rankingPoints));
  const hasFlatOpportunity = backendRanking
    ? ["exact_tie", "similar"].includes(timeline.modeledPeriod?.comparison)
    : hasFlatDisplayedOpportunity(rankingPoints);
  const missingWaterTemperature = allPoints.some((point) =>
    Array.isArray(point.missingInputs)
      ? point.missingInputs.includes("water_temperature")
      : false
  );
  const hasChartSeries = points.some(
    (point) => isScore(point.fishingOpportunity) || isScore(point.activity)
  );
  const hasActivitySeries = points.some((point) => isScore(point.activity));

  return (
    <section className="rp-species-timeline" aria-labelledby="species-hourly-title">
      <div className="rp-species-timeline__heading">
        <div>
          <h4 id="species-hourly-title">
            {t(distinctLegacy ? "mackerel_availability_outlook_hourly_title" : "fishing_today_intelligence_hourly_outlook")}
          </h4>
          <p id={regional ? "species-hourly-description" : undefined}>{t(distinctLegacy ? "mackerel_availability_outlook_hourly_description" : "fishing_today_intelligence_hourly_outlook_description")}</p>
          <p>{t("fishing_today_intelligence_hourly_axis_note")}</p>
        </div>
        <div className="rp-species-timeline__legend" aria-label={t("fishing_today_intelligence_timeline_legend")}>
          <span>
            <i className="rp-species-timeline__legend-line" />
            {t(distinctLegacy ? "mackerel_legacy_index" : "fishing_today_intelligence_opportunity")}
          </span>
          {hasActivitySeries ? (
            <span>
              <i className="rp-species-timeline__legend-line rp-species-timeline__legend-line--activity" />
              {t("fishing_today_intelligence_activity")}
            </span>
          ) : null}
        </div>
      </div>

      {!points.length ? (
        <p className="rp-species-timeline__unavailable" role="status">
          {t("fishing_today_intelligence_hourly_data_unavailable")}
        </p>
      ) : (
        <>
          <div
            className="rp-species-timeline__chart"
            role="group"
            aria-label={t(distinctLegacy ? "mackerel_availability_outlook_hourly_title" : "fishing_today_intelligence_timeline_chart_aria")}
            aria-describedby={regional ? "species-hourly-description" : undefined}
          >
            <svg viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`} preserveAspectRatio="none" aria-hidden="true">
              {bands.map((band, index) => (
                <rect key={`${band.kind}-${index}`} data-light-cycle={band.kind}
                  className={`rp-species-timeline__light-band rp-species-timeline__light-band--${band.kind}`}
                  x={dayX(band.start, day.axis)} width={dayX(band.end, day.axis) - dayX(band.start, day.axis)}
                  y={CHART_MARGIN.top} height={CHART_HEIGHT - CHART_MARGIN.top - CHART_MARGIN.bottom} />
              ))}
              {midnights.map((boundary) => <line key={boundary.timestampMs} className="rp-species-timeline__midnight"
                x1={dayX(boundary.timestampMs, day.axis)} x2={dayX(boundary.timestampMs, day.axis)}
                y1={CHART_MARGIN.top} y2={CHART_HEIGHT - CHART_MARGIN.bottom} />)}
              {Y_TICKS.map((tick) => {
                const y = scoreToChartY(tick);
                return (
                  <g key={tick}>
                    <line className="rp-species-timeline__grid" x1={CHART_MARGIN.left} x2={CHART_WIDTH - CHART_MARGIN.right} y1={y} y2={y} />
                  </g>
                );
              })}
              <Series field="fishingOpportunity" name="opportunity" points={points} />
              <Series field="activity" name="activity" points={points} />
              {currentPoint ? (
                <g className="rp-species-timeline__now">
                  <line x1={currentPoint.x} x2={currentPoint.x} y1={CHART_MARGIN.top} y2={CHART_HEIGHT - CHART_MARGIN.bottom} />
                  <text x={currentPoint.x} y={12} textAnchor="middle">{t("fishing_today_intelligence_now")}</text>
                </g>
              ) : null}
              {selectedPoint ? (
                <line className="rp-species-timeline__selection" x1={selectedPoint.x} x2={selectedPoint.x} y1={CHART_MARGIN.top} y2={CHART_HEIGHT - CHART_MARGIN.bottom} />
              ) : null}
            </svg>
            <div className="rp-species-timeline__score-axis" aria-hidden="true">
              {Y_TICKS.map((tick) => <span key={tick} style={{ left: `calc(${CHART_MARGIN.left / CHART_WIDTH * 100}% - 4px)`, top: `${scoreToChartY(tick) / CHART_HEIGHT * 100}%` }}>{tick}</span>)}
            </div>
            <div className="rp-species-timeline__time-axis" aria-hidden="true">
              {day.axis.ticks.map(({ hour, timestampMs }) => <span key={timestampMs} style={{ left: `${dayX(timestampMs, day.axis) / CHART_WIDTH * 100}%` }}>{windowStart ? formatForecastHour(new Date(timestampMs).toISOString(), forecastTimezone, locale, true) : formatLocalDayTick(hour, locale)}</span>)}
            </div>
            <div className="rp-species-timeline__date-axis" aria-hidden="true">
              {midnights.map((boundary) => <span key={boundary.timestampMs} style={{ left: `${dayX(boundary.timestampMs, day.axis) / CHART_WIDTH * 100}%` }}>{new Intl.DateTimeFormat(locale, { timeZone: forecastTimezone, month: "short", day: "numeric" }).format(boundary.timestampMs)}</span>)}
            </div>
            <div className="rp-species-timeline__controls">
              {points.map((point, index) => {
                const anchorScore = isScore(point.fishingOpportunity)
                  ? graphScore(point.fishingOpportunity)
                  : isScore(point.activity)
                    ? graphScore(point.activity)
                    : 50;
                const time = displayHour(point);
                // Partition hourly hit areas, including repeated DST hours.
                const neighborDistance = Math.min(
                  index > 0 ? point.x - points[index - 1].x : CHART_WIDTH,
                  index + 1 < points.length ? points[index + 1].x - point.x : CHART_WIDTH
                );
                return (
                  <button
                    aria-label={pointAriaLabel(point, time, t, regional)}
                    aria-pressed={point.timestamp === selectedPoint?.timestamp}
                    className="rp-species-timeline__point-control"
                    data-activity-score={isScore(point.activity) ? point.activity.score : undefined}
                    data-opportunity-score={isScore(point.fishingOpportunity) ? point.fishingOpportunity.score : undefined}
                    key={point.timestamp}
                    onClick={() => setSelectedTimestamp(point.timestamp)}
                    onKeyDown={(event) => {
                      const next = event.key === "ArrowRight" ? index + 1 : event.key === "ArrowLeft" ? index - 1 : null;
                      if (next !== null && points[next]) {
                        event.preventDefault();
                        setSelectedTimestamp(points[next].timestamp);
                        event.currentTarget.parentElement.children[next].focus();
                      }
                    }}
                    style={{
                      left: `${(point.x / CHART_WIDTH) * 100}%`,
                      top: `${(scoreToChartY(anchorScore) / CHART_HEIGHT) * 100}%`,
                      width: neighborDistance > 0 ? `min(2rem, ${neighborDistance / CHART_WIDTH * 100}%)` : undefined,
                    }}
                    type="button"
                  />
                );
              })}
            </div>
            {!hasChartSeries ? (
              <p className="rp-species-timeline__chart-empty">
                {t("fishing_today_intelligence_hourly_data_unavailable")}
              </p>
            ) : null}
          </div>

          {bands.length ? (
            <div className="rp-species-timeline__light-context">
              <div className="rp-species-timeline__light-legend">
                {[...new Set(bands.map((band) => band.kind))].map((kind) => (
                  <span key={kind}><i className={`rp-species-timeline__light-swatch rp-species-timeline__light-band--${kind}`} />{t(`fishing_today_intelligence_light_${kind}`)}</span>
                ))}
              </div>
              <p>{t("fishing_today_intelligence_light_note")}</p>
            </div>
          ) : <p className="rp-species-timeline__note">{t("fishing_today_intelligence_light_unavailable")}</p>}
          {bands.length > 0 && lightContext.missing ? <p className="rp-species-timeline__note">{t("fishing_today_intelligence_light_unavailable")}</p> : null}
          {!day.complete ? <p className="rp-species-timeline__note" role="status">{t(windowStart ? "fishing_today_intelligence_window_partial" : "fishing_today_intelligence_chart_partial")}</p> : null}

          {selectedPoint ? (
            <SelectedDetails regional={regional} point={selectedPoint} t={t} time={displayHour(selectedPoint)} />
          ) : null}

          {missingWaterTemperature ? (
            <p className="rp-species-timeline__note" role="status">
              {t("fishing_today_intelligence_water_temperature_hourly_missing")}
            </p>
          ) : null}

          <div className="rp-species-timeline__highest">
            <strong>
              {t(
                mackerelRanking ? distinctLegacy ? "mackerel_selected_outlook_period" : "mackerel_selected_period" : hasFlatOpportunity
                  ? "fishing_today_intelligence_no_standout_period"
                  : backendRanking ? "sea_bass_modeled_period" : highestHoursLabelKey
              )}
            </strong>
            {mackerelRanking && timeline.modeledPeriod ? (
              <p>{t(timeline.modeledPeriod.kind === "period" ? mackerelMeanLabel : distinctLegacy ? "mackerel_legacy_index" : "mackerel_regional_availability")} · {scoreText(timeline.modeledPeriod.meanOpportunity, t)}</p>
            ) : null}
            {mackerelRanking && hasFlatOpportunity ? (
              <p>{t(distinctLegacy ? "mackerel_similar_outlook_periods" : "mackerel_similar_regional_periods")}</p>
            ) : null}
            {hasFlatOpportunity && !mackerelRanking ? (
              <p>{t(distinctLegacy ? "mackerel_similar_legacy" : backendRanking ? "sea_bass_similar_periods" : "fishing_today_intelligence_similar_opportunity_period")}</p>
            ) : highest.length ? (
              <ol>
                {highest.map((point) => (
                  <li key={point.timestamp}>
                    <button type="button" onClick={() => setSelectedTimestamp(point.timestamp)}>
                      <time dateTime={point.timestamp}>{displayHour(point, true)}</time>
                      <span>{scoreText(point.fishingOpportunity, t)}</span>
                    </button>
                  </li>
                ))}
              </ol>
            ) : (
              <p>{t(mackerelRanking ? "mackerel_insufficient_period_hours" : "fishing_today_intelligence_no_opportunity_hours")}</p>
            )}
            {mackerelRanking && timeline.alternativePeriod ? (
              <p>
                {t("mackerel_alternative_period")} · {t(timeline.alternativePeriod.kind === "period" ? mackerelMeanLabel : distinctLegacy ? "mackerel_legacy_index" : "mackerel_regional_availability")}
                <button type="button" onClick={() => setSelectedTimestamp(timeline.alternativePeriod.pointTimestamps[0])}>
                  <time dateTime={timeline.alternativePeriod.startTimestamp}>{formatForecastHour(timeline.alternativePeriod.startTimestamp, forecastTimezone, locale)}</time>
                  {timeline.alternativePeriod.kind === "period" ? `–${formatForecastHour(timeline.alternativePeriod.endTimestamp, forecastTimezone, locale)}` : ""}
                  {" · "}{scoreText(timeline.alternativePeriod.meanOpportunity, t)}
                </button>
              </p>
            ) : null}
          </div>
        </>
      )}
    </section>
  );
}
